import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { auto } from '@popperjs/core';
import { Exercise, RoutineModelExercise } from 'src/app/interfaces/exercise';
import { MuscleGroup } from 'src/app/interfaces/muscle-group';
import { User } from 'src/app/interfaces/user';
import { ExerciseService } from 'src/app/services/exercise.service';
import { RoutineService } from 'src/app/services/routine.service';
import { StateService } from 'src/app/services/state.service';
import { TabletRoutinesService } from 'src/app/services/tablet-routines.service';
import { ToastService } from 'src/app/services/toast.service';
import { UserService } from 'src/app/services/user.service';

@Component({
  selector: 'app-tablet-routine-form',
  templateUrl: './tablet-routine-form.component.html',
  styleUrls: ['./tablet-routine-form.component.scss'],
})
export class TabletRoutineFormComponent implements OnInit {
  routineForm: FormGroup;
  forbiddenExerciseForm: FormGroup;

  muscleGroups: MuscleGroup[] = [];
  userExperienceLevels: any;
  forbiddenExercisesList: Exercise[] = [];
  addingForbiddenExercise: boolean = false;
  exercises: Exercise[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastService: ToastService,
    private exerciseService: ExerciseService,
    private userService: UserService,
    private tabletRoutinesService: TabletRoutinesService,
    private stateService: StateService
  ) {
    this.routineForm = this.fb.group({
      userName: ['', Validators.required],
      gender: ['', Validators.required],
      trainingExperienceMonths: [
        null,
        [Validators.pattern('^[0-9]+$'), Validators.required],
      ],
      muscleGroupPriority: [null, [Validators.required]],
      expectedTrainingTime: [
        null,
        [Validators.pattern('^[0-9]+$'), Validators.required],
      ],
    });

    this.forbiddenExerciseForm = this.fb.group({
      forbiddenExercise: [null, Validators.required],
      forbiddenMuscleGroup: [null, Validators.required],
    });
  }

  ngOnInit(): void {
    this.getMuscleGroups();
    this.userExperienceLevels = this.userService.userExperienceLevels;

    const modalHtmlElement = document.getElementById('routineFormTablet')!;

    modalHtmlElement.addEventListener('hidden.bs.shown', () => {
      this.resetForm();
    });
  }

  startAddingForbiddenExercise() {
    this.addingForbiddenExercise = true;
  }

  getMuscleGroups() {
    this.exerciseService.getMuscleGroups().subscribe({
      next: (muscleGroups: any) => {
        this.muscleGroups = muscleGroups.muscleGroup;
      },
    });
  }

  getMuscleGroupExercises(muscleGroupId: number) {
    this.exerciseService.getExerciseByMuscleGroup(muscleGroupId).subscribe({
      next: (response: any) => {
        this.exercises = response.exercises;

        this.exercises.forEach((e) => (e.muscleGroupId = muscleGroupId));
      },
      error: (_error: any) => {
        this.toastService.alert(
          'No pudimos traer los ejercicios de la base de datos. Si el error continua contactate con el soporte.'
        );
      },
    });
  }

  onSelectMuscleGroup(ev: any) {
    const muscleGroupId = ev.target.value;

    this.getMuscleGroupExercises(muscleGroupId);
  }

  removeForbiddenExercise(exerciseId: number) {
    this.forbiddenExercisesList = this.forbiddenExercisesList.filter(
      (e) => e.id !== exerciseId
    );
  }

  addForbiddenExercise() {
    const forbiddenExercise: Exercise = this.exercises.filter(
      (e) => e.id === parseInt(this.forbiddenExercise.value)
    )[0] as Exercise;

    this.forbiddenExercisesList.push(forbiddenExercise);

    this.forbiddenExerciseForm.reset();
    this.forbiddenExercise.setValue(null);
    this.forbiddenMuscleGroup.setValue(null);
    this.addingForbiddenExercise = false;
  }

  resetForm() {
    this.forbiddenExerciseForm.reset();
    this.forbiddenExercise.setValue(null);
    this.forbiddenMuscleGroup.setValue(null);
    this.addingForbiddenExercise = false;

    this.userName.setValue(null);
    this.gender.setValue(null);
    this.trainingExperienceMonths.setValue(null);
    this.muscleGroupPriority.setValue(null);
    this.expectedTrainingTime.setValue(null);
  }

  createRoutine() {
    let autoassignedRoutineData = this.routineForm.value;

    if (autoassignedRoutineData['forbiddenExercises'] === undefined) {
      autoassignedRoutineData['forbiddenExercises'] = [];
    }

    this.forbiddenExercisesList.forEach((e) => {
      autoassignedRoutineData['forbiddenExercises'].push(e.id);
    });

    autoassignedRoutineData['expectedTrainingTime'] = parseInt(
      autoassignedRoutineData['expectedTrainingTime']
    );
    autoassignedRoutineData['trainingExperienceMonths'] = parseInt(
      autoassignedRoutineData['trainingExperienceMonths']
    );

    this.tabletRoutinesService
      .createAutoAssignedRoutine(autoassignedRoutineData)
      .subscribe({
        next: (response: any) => {
          if (response.id === 0) {
            this.router.navigate(['/home-tablet']);

            window.alert(
              'No hemos podido crear la rutina ya que no hay una coincidencia con la base de datos'
            );
          } else {
            this.toastService.success('Rutina creada correctamente');
            this.routineForm.reset();
            this.forbiddenExercisesList = [];
            this.stateService.openModal.hide();
            this.resetForm();
          }
        },
        error: () => {
          this.toastService.alert('No se pudo crear la rutina');
        },
      });
  }

  enableExercises() {
    this.forbiddenExercise.enable();
  }

  get forbiddenExercise() {
    return this.forbiddenExerciseForm.controls['forbiddenExercise'];
  }

  get forbiddenMuscleGroup() {
    return this.forbiddenExerciseForm.controls['forbiddenMuscleGroup'];
  }

  get userName() {
    return this.routineForm.controls['userName'];
  }

  get gender() {
    return this.routineForm.controls['gender'];
  }

  get trainingExperienceMonths() {
    return this.routineForm.controls['trainingExperienceMonths'];
  }

  get muscleGroupPriority() {
    return this.routineForm.controls['muscleGroupPriority'];
  }

  get expectedTrainingTime() {
    return this.routineForm.controls['expectedTrainingTime'];
  }
}
